// /app/frontend/src/components/configurator/QuoteBuilder.jsx
// Customer Quote Builder - Save visualizations and request formal quote

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, Trash2, FileText, Send, User, Mail, Phone, MapPin,
  Building2, MessageSquare, Check, Plus, Image, X, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { currentSource } from '../../lib/consultSource';
import { brandLabel } from '../../lib/brandNames';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuoteBuilder = ({ 
  open, 
  onOpenChange,
  currentVisualization,
  selectedColor,
  selectedBrand,
  selectedCollection,
  selectedMaterial,
  config,
  productType,
  canAdd = true,
  onItemsChange
}) => {
  const [quoteItems, setQuoteItems] = useState([]);
  const [step, setStep] = useState('items'); // 'items' | 'details' | 'confirm'
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Customer details form
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    preferred_contact: 'email',
    project_timeline: 'within_month'
  });
  
  // Load saved quote items from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('apollo_quote_items');
    if (saved) {
      setQuoteItems(JSON.parse(saved));
    }
  }, []);
  
  // Save quote items to localStorage
  useEffect(() => {
    localStorage.setItem('apollo_quote_items', JSON.stringify(quoteItems));
    onItemsChange?.(quoteItems.length > 0);
  }, [quoteItems, onItemsChange]);
  
  // Add current configuration to quote
  const addToQuote = () => {
    if (!selectedColor) {
      toast.error('Please select a color first');
      return;
    }
    if (!canAdd) {
      toast.error('Complete all steps and choose a room before adding to your quote');
      return;
    }
    
    const newItem = {
      id: Date.now(),
      product_type: productType,
      brand_name: selectedBrand?.name,
      collection_name: selectedCollection?.name,
      material_name: selectedMaterial?.name,
      color_name: selectedColor.name,
      color_id: selectedColor.id,
      color_hex: selectedColor.hex_color,
      visualization_url: currentVisualization?.image_url 
        ? `${process.env.REACT_APP_BACKEND_URL}${currentVisualization.image_url}`
        : null,
      visualization_title: currentVisualization?.title,
      config: { ...config },
      added_at: new Date().toISOString()
    };
    
    setQuoteItems(prev => [...prev, newItem]);
    toast.success(`Added ${selectedColor.name} to quote`);
  };
  
  // Remove item from quote
  const removeItem = (itemId) => {
    setQuoteItems(prev => prev.filter(item => item.id !== itemId));
    toast.success('Item removed from quote');
  };
  
  // Clear all items
  const clearQuote = () => {
    setQuoteItems([]);
    toast.success('Quote cleared');
  };
  
  // Submit quote request
  const submitQuote = async () => {
    if (quoteItems.length === 0) {
      toast.error('Please add at least one item to your quote');
      return;
    }
    
    if (!customerDetails.name || !customerDetails.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/quotes/request`, {
        customer: customerDetails,
        items: quoteItems,
        submitted_at: new Date().toISOString(),
        ...currentSource('Configurator: Request Quote')
      });
      
      setSubmitted(true);
      setStep('confirm');
      // Clear saved items after successful submission
      localStorage.removeItem('apollo_quote_items');
      setQuoteItems([]);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Failed to submit quote:', error);
      toast.error('Failed to submit quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const formatProductType = (type) => {
    return type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {step === 'items' && 'Quote Builder'}
            {step === 'details' && 'Your Details'}
            {step === 'confirm' && 'Quote Submitted!'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4">
          {/* Step 1: Quote Items */}
          {step === 'items' && (
            <div className="space-y-4">
              {/* Current Selection - Add to Quote */}
              {selectedColor && (
                <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-start gap-4">
                    {currentVisualization?.image_url && (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${currentVisualization.image_url}`}
                        alt="Current visualization"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-neutral-900">Current Selection</h4>
                      <p className="text-sm text-neutral-600 mt-1">
                        {selectedBrand?.name} → {selectedCollection?.name} 
                        {selectedMaterial?.name && ` → ${selectedMaterial.name}`} → {selectedColor.name}
                      </p>
                      <p className="text-xs text-neutral-500 mt-1">
                        {formatProductType(productType)}{config?.room_label ? ` · ${config.room_label}` : ''}{config?.width && config?.height ? ` · ${config.width}" × ${config.height}"` : ''}
                      </p>
                      {!canAdd && <p className="text-xs text-amber-700 mt-1" data-testid="quote-add-blocked-hint">Complete all steps and choose a room to add this shade</p>}
                    </div>
                    <Button 
                      onClick={addToQuote}
                      disabled={!canAdd}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="quote-add-current-button"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add to Quote
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Quote Items List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-neutral-800">
                    Quote Items ({quoteItems.length})
                  </h4>
                  {quoteItems.length > 0 && (
                    <button 
                      onClick={clearQuote}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {quoteItems.length === 0 ? (
                  <div className="text-center py-8 text-neutral-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No items in your quote yet</p>
                    <p className="text-sm mt-1">Configure a shade and add it to your quote</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {quoteItems.map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="border rounded-lg p-3 bg-white hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {item.visualization_url ? (
                            <img 
                              src={item.visualization_url}
                              alt={item.color_name}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div 
                              className="w-16 h-16 rounded flex items-center justify-center"
                              style={{ backgroundColor: item.color_hex || '#ccc' }}
                            >
                              <Image className="w-6 h-6 text-white/50" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-neutral-900 truncate">
                              {item.color_name}
                            </p>
                            <p className="text-xs text-neutral-500 truncate">
                              {brandLabel(item.brand_name)} → {item.collection_name}
                              {item.material_name && ` → ${item.material_name}`}
                            </p>
                            <p className="text-xs text-neutral-400">
                              {formatProductType(item.product_type)}{item.config?.room_label ? ` · ${item.config.room_label}` : ''}{item.config?.width && item.config?.height ? ` · ${item.config.width}" × ${item.config.height}"` : ''}
                            </p>
                          </div>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 2: Customer Details */}
          {step === 'details' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Full Name *</Label>
                  <Input
                    value={customerDetails.name}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Email *</Label>
                  <Input
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Phone</Label>
                  <Input
                    value={customerDetails.phone}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Company (optional)</Label>
                  <Input
                    value={customerDetails.company}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company name"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm">Address</Label>
                <Input
                  value={customerDetails.address}
                  onChange={(e) => setCustomerDetails(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Main St"
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm">City</Label>
                  <Input
                    value={customerDetails.city}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">State</Label>
                  <Input
                    value={customerDetails.state}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="State"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">ZIP</Label>
                  <Input
                    value={customerDetails.zip}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, zip: e.target.value }))}
                    placeholder="12345"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm">Project Timeline</Label>
                <select
                  value={customerDetails.project_timeline}
                  onChange={(e) => setCustomerDetails(prev => ({ ...prev, project_timeline: e.target.value }))}
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                >
                  <option value="asap">As soon as possible</option>
                  <option value="within_month">Within a month</option>
                  <option value="1_3_months">1-3 months</option>
                  <option value="3_plus_months">3+ months</option>
                  <option value="just_browsing">Just browsing</option>
                </select>
              </div>
              
              <div>
                <Label className="text-sm">Additional Notes</Label>
                <Textarea
                  value={customerDetails.notes}
                  onChange={(e) => setCustomerDetails(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any specific requirements, window sizes, installation needs..."
                  className="mt-1 h-24"
                />
              </div>
              
              <div>
                <Label className="text-sm">Preferred Contact Method</Label>
                <div className="flex gap-4 mt-2">
                  {['email', 'phone', 'either'].map(method => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contact_method"
                        value={method}
                        checked={customerDetails.preferred_contact === method}
                        onChange={(e) => setCustomerDetails(prev => ({ ...prev, preferred_contact: e.target.value }))}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span className="text-sm capitalize">{method}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Confirmation */}
          {step === 'confirm' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                Quote Request Submitted!
              </h3>
              <p className="text-neutral-600 mb-6">
                Thank you for your interest in Apollo Shades. Our team will review your quote request
                and get back to you within 1-2 business days.
              </p>
              <div className="bg-neutral-50 rounded-lg p-4 text-left max-w-md mx-auto">
                <h4 className="font-medium text-neutral-800 mb-2">What happens next?</h4>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>We'll review your selections and project requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>A specialist will contact you with pricing and options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Schedule a consultation or measurement if needed</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4">
          {step === 'items' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button 
                onClick={() => setStep('details')}
                disabled={quoteItems.length === 0}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Continue to Details
              </Button>
            </>
          )}
          
          {step === 'details' && (
            <>
              <Button variant="outline" onClick={() => setStep('items')}>
                Back
              </Button>
              <Button 
                onClick={submitQuote}
                disabled={submitting || !customerDetails.name || !customerDetails.email}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Quote Request
                  </>
                )}
              </Button>
            </>
          )}
          
          {step === 'confirm' && (
            <Button onClick={() => {
              setStep('items');
              setSubmitted(false);
              onOpenChange(false);
            }}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteBuilder;
